import React from "react";
import {
  Typography,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  FormHelperText,
  Checkbox,
  Box,
  Autocomplete,
  ListItemText,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { payrollOptions } from "../data/payrollOptions";
import CalendarGridTable from "./CalendarGridTable";

const sectionTitleSx = {
  color: "#7A7A7A",
  fontWeight: "600",
  fontSize: "14px",
  my: 0.5,
};

const paperSx = {
  p: { xs: 1.5, sm: 2 },
  borderRadius: 2,
  mx: { xs: 0, sm: 1, md: 2 },
  mb: 2,
};

export default function Payroll({ isStaff }) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const hasMonthlyAdvance = watch("hasMonthlyAdvance");
  const advanceCalcBaseForCommissioned = watch("advanceCalcBaseForCommissioned");
  const irrfAdvanceRetentionRule = watch("irrfAdvanceRetentionRule");
  const thirteenthFirstInstallmentWithAverages = watch(
    "thirteenthFirstInstallmentWithAverages",
  );
  const internProLaboreThirteenth = watch("internProLaboreThirteenth");
  const internProLaboreThirteenthRubricsNovDec = watch(
    "internProLaboreThirteenthRubricsNovDec",
  );
  const plrAccordingToCctAct = watch("plrAccordingToCctAct");
  const consignedLoanDeductionLaw = watch("consignedLoanDeductionLaw");
  const consignedLoanFullDeductionIfNoBalance = watch(
    "consignedLoanFullDeductionIfNoBalance",
  );
  const anticipatePaymentsOnWeekendsHolidays = watch(
    "anticipatePaymentsOnWeekendsHolidays",
  );
  const hasPlr = watch("hasPlr");
  const plrFrequencyAndCompetencies = watch("plrFrequencyAndCompetencies");
  const hasConsignedLoan = watch("hasConsignedLoan");
  const calendarDefaultValues = watch("calendarDefaultValues");
  const payrollIsValidated = watch("payrollIsValidated");

  return (
    <>
      <Paper elevation={2} sx={paperSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" sx={sectionTitleSx}>
            Folha de pagamento
          </Typography>
          {isStaff && (
            <Controller
              name="payrollIsValidated"
              control={control}
              render={({ field }) => (
                <FormControl required error={!!errors.payrollIsValidated}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      {...field}
                      checked={!!payrollIsValidated}
                      size="small"
                      inputProps={{ "aria-label": "controlled" }}
                    />
                    <Typography variant="body2">Validado</Typography>
                  </Box>
                  <FormHelperText>
                    {errors.payrollIsValidated?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          )}
        </Box>
        <CalendarGridTable
          control={control}
          initialData={calendarDefaultValues}
          isDisabled={payrollIsValidated}
        />
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid
            item
            xs={12}
            md={anticipatePaymentsOnWeekendsHolidays === "1" ? 6 : 12}
          >
            <Controller
              name="anticipatePaymentsOnWeekendsHolidays"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.anticipatePaymentsOnWeekendsHolidays}
                >
                  <InputLabel id="anticipate-payments-label">
                    Antecipa pagamentos quando cair em final de semana e feriados
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="anticipate-payments-label"
                    label="Antecipa pagamentos quando cair em final de semana e feriados"
                  >
                    {payrollOptions.anticipatePaymentsOnWeekendsHolidays.map(
                      opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.anticipatePaymentsOnWeekendsHolidays?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          {anticipatePaymentsOnWeekendsHolidays === "1" && (
            <Grid item xs={12} md={6}>
              <Controller
                name="anticipatePaymentsDescription"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required disabled={payrollIsValidated}
                    label="Descreva a Regra de Negócio"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!errors.anticipatePaymentsDescription}
                    helperText={errors.anticipatePaymentsDescription?.message}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Adiantamento
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasMonthlyAdvance"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasMonthlyAdvance}>
                  <InputLabel id="has-monthly-advance-label">
                    Possui adiantamento mensal
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="has-monthly-advance-label"
                    label="Possui adiantamento mensal"
                  >
                    {payrollOptions.hasMonthlyAdvance.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.hasMonthlyAdvance?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasMonthlyAdvance === "1" && (
            <>
              <Grid item xs={12} md={6}>
                <Controller
                  name="advancePercentage"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={payrollIsValidated}
                      label="Informe o percentual (1-100)"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 1, max: 100 }}
                      error={!!errors.advancePercentage}
                      helperText={errors.advancePercentage?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="advanceCalcBase"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.advanceCalcBase}>
                      <InputLabel id="advance-calc-base-label">
                        Base de cálculo
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="advance-calc-base-label"
                        label="Base de cálculo"
                      >
                        {payrollOptions.advanceCalcBase.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.advanceCalcBase?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={advanceCalcBaseForCommissioned === "3" ? 6 : 12}>
                <Controller
                  name="advanceCalcBaseForCommissioned"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.advanceCalcBaseForCommissioned}
                    >
                      <InputLabel id="advance-calc-base-for-commissioned-label">
                        Base de cálculo para Comissionados
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="advance-calc-base-for-commissioned-label"
                        label="Base de cálculo para Comissionados"
                      >
                        {payrollOptions.advanceCalcBaseForCommissioned.map(
                          opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                      <FormHelperText>
                        {errors.advanceCalcBaseForCommissioned?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              {advanceCalcBaseForCommissioned === "3" && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="advanceCalcBaseForCommissionedOther"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required disabled={payrollIsValidated}
                        label="Descreva a Regra de Negócio"
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={!!errors.advanceCalcBaseForCommissionedOther}
                        helperText={
                          errors.advanceCalcBaseForCommissionedOther?.message
                        }
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Controller
                  name="minDaysWorkedForAdvance"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={payrollIsValidated}
                      label="Quantos dias mínimos trabalhados no mês (1-30):"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 1, max: 30 }}
                      error={!!errors.minDaysWorkedForAdvance}
                      helperText={errors.minDaysWorkedForAdvance?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="advancePaymentCalculationType"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.advancePaymentCalculationType}
                    >
                      <InputLabel id="advance-payment-calculation-type-label">
                        Pagamento
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="advance-payment-calculation-type-label"
                        label="Pagamento"
                      >
                        {payrollOptions.advancePaymentCalculationType.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.advancePaymentCalculationType?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="newHiresReceiveAdvance"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.newHiresReceiveAdvance}
                    >
                      <InputLabel id="new-hires-receive-advance-label">
                        Recebem adiantamento no mês da admissão
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="new-hires-receive-advance-label"
                        label="Recebem adiantamento no mês da admissão"
                      >
                        {payrollOptions.newHiresReceiveAdvance.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.newHiresReceiveAdvance?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="advanceAdhesionNextMonth"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.advanceAdhesionNextMonth}
                    >
                      <InputLabel id="advance-adhesion-next-month-label">
                        A adesão no mês seguinte
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="advance-adhesion-next-month-label"
                        label="A adesão no mês seguinte"
                      >
                        {payrollOptions.advanceAdhesionNextMonth.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.advanceAdhesionNextMonth?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="maternityLeaveReceiveAdvance"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.maternityLeaveReceiveAdvance}
                    >
                      <InputLabel id="maternity-leave-receive-advance-label">
                        Colaborador em Licença Maternidade recebe adiantamento?
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="maternity-leave-receive-advance-label"
                        label="Colaborador em Licença Maternidade recebe adiantamento?"
                      >
                        {payrollOptions.maternityLeaveReceiveAdvance.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.maternityLeaveReceiveAdvance?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="proLaboreReceiveAdvance"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.proLaboreReceiveAdvance}
                    >
                      <InputLabel id="pro-labore-receive-advance-label">
                        Pró-labore recebe adiantamento?
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="pro-labore-receive-advance-label"
                        label="Pró-labore recebe adiantamento?"
                      >
                        {payrollOptions.proLaboreReceiveAdvance.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.proLaboreReceiveAdvance?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="internReceiveAdvance"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.internReceiveAdvance}
                    >
                      <InputLabel id="intern-receive-advance-label">
                        Estagiário recebe adiantamento?
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="intern-receive-advance-label"
                        label="Estagiário recebe adiantamento?"
                      >
                        {payrollOptions.internReceiveAdvance.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.internReceiveAdvance?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="apprenticeReceiveAdvance"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.apprenticeReceiveAdvance}
                    >
                      <InputLabel id="apprentice-receive-advance-label">
                        Aprendiz recebe adiantamento?
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="apprentice-receive-advance-label"
                        label="Aprendiz recebe adiantamento?"
                      >
                        {payrollOptions.apprenticeReceiveAdvance.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.apprenticeReceiveAdvance?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={irrfAdvanceRetentionRule === "3" ? 6 : 12}>
                <Controller
                  name="irrfAdvanceRetentionRule"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.irrfAdvanceRetentionRule}
                    >
                      <InputLabel id="irrf-advance-retention-rule-label">
                        Retenção IRRF - Processa a retenção do Imposto de Renda
                        na Fonte (IRRF) sobre a parcela do adiantamento salarial
                        (RIR/2018, arts. 677 e 678, § 2º)?
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="irrf-advance-retention-rule-label"
                        label="Retenção IRRF - Processa a retenção do Imposto de Renda na Fonte (IRRF) sobre a parcela do adiantamento salarial (RIR/2018, arts. 677 e 678, § 2º)?"
                      >
                        {payrollOptions.irrfAdvanceRetentionRule.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.irrfAdvanceRetentionRule?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              {irrfAdvanceRetentionRule === "3" && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="irrfAdvanceRetentionRuleOther"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required disabled={payrollIsValidated}
                        label="Descreva detalhadamente"
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={!!errors.irrfAdvanceRetentionRuleOther}
                        helperText={errors.irrfAdvanceRetentionRuleOther?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Controller
                  name="advanceAnalyticalReportOrder"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.advanceAnalyticalReportOrder}
                    >
                      <InputLabel id="advance-analytical-report-order-label">
                        Relatório Analítico por ordem de
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="advance-analytical-report-order-label"
                        label="Relatório Analítico por ordem de"
                      >
                        {payrollOptions.advanceAnalyticalReportOrder.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.advanceAnalyticalReportOrder?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="advanceNetReport"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.advanceNetReport}
                    >
                      <InputLabel id="advance-net-report-label">
                        Relatório Líquido - Consolidado (matriz|filial)
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="advance-net-report-label"
                        label="Relatório Líquido - Consolidado (matriz|filial)"
                      >
                        {payrollOptions.advanceNetReport.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.advanceNetReport?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="advanceNetReportExcel"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.advanceNetReportExcel}
                    >
                      <InputLabel id="advance-net-report-excel-label">
                        Relatório Líquido no formato em Excel
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="advance-net-report-excel-label"
                        label="Relatório Líquido no formato em Excel"
                      >
                        {payrollOptions.advanceNetReportExcel.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.advanceNetReportExcel?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Provisão de Férias e 13º Salário
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="thirteenthFirstInstallmentWriteOff"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.thirteenthFirstInstallmentWriteOff}
                >
                  <InputLabel id="thirteenth-first-installment-write-off-label">
                    Realiza baixa da 1ª parcela (férias e antecipações em geral)
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="thirteenth-first-installment-write-off-label"
                    label="Realiza baixa da 1ª parcela (férias e antecipações em geral)"
                  >
                    {payrollOptions.thirteenthFirstInstallmentWriteOff.map(
                      opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.thirteenthFirstInstallmentWriteOff?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="vacationWriteOffMethod"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.vacationWriteOffMethod}
                >
                  <InputLabel id="vacation-write-off-method-label">
                    Férias
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="vacation-write-off-method-label"
                    label="Férias"
                  >
                    {payrollOptions.vacationWriteOffMethod.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.vacationWriteOffMethod?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="resignationProvisionWhenTerminated"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.resignationProvisionWhenTerminated}
                >
                  <InputLabel id="resignation-provision-when-terminated-label">
                    Apura o provisionamento de Férias e 13º Salário no mês de
                    demissão do empregado (*)
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="resignation-provision-when-terminated-label"
                    label="Apura o provisionamento de Férias e 13º Salário no mês de demissão do empregado (*)"
                  >
                    {payrollOptions.resignationProvisionWhenTerminated.map(
                      opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.resignationProvisionWhenTerminated?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
            <Typography
              sx={{ fontSize: "8px", color: "#7A7A7A", fontWeight: "400", ml: 1, mt: 0.5 }}
            >
              (*) A configuração padrão N (Não) indica ao sistema para não
              calcular a provisão no mês de demissão. Com isso, o sistema
              apresentará como baixa de provisão do mês anterior.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="provisionAnalyticalReportOrder"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.provisionAnalyticalReportOrder}
                >
                  <InputLabel id="provision-analytical-report-order-label">
                    Relatório RH/DP Analítico por ordem de
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="provision-analytical-report-order-label"
                    label="Relatório RH/DP Analítico por ordem de"
                  >
                    {payrollOptions.provisionAnalyticalReportOrder.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.provisionAnalyticalReportOrder?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="provisionSyntheticReportOrder"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.provisionSyntheticReportOrder}
                >
                  <InputLabel id="provision-synthetic-report-order-label">
                    Relatório RH/DP Sintético por ordem de
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="provision-synthetic-report-order-label"
                    label="Relatório RH/DP Sintético por ordem de"
                  >
                    {payrollOptions.provisionSyntheticReportOrder.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.provisionSyntheticReportOrder?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="provisionAccountingSyntheticReportOrder"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.provisionAccountingSyntheticReportOrder}
                >
                  <InputLabel id="provision-accounting-synthetic-report-order-label">
                    Relatório Contabilidade Sintético por ordem de
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="provision-accounting-synthetic-report-order-label"
                    label="Relatório Contabilidade Sintético por ordem de"
                  >
                    {payrollOptions.provisionAccountingSyntheticReportOrder.map(
                      opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.provisionAccountingSyntheticReportOrder?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          13º Salário
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="thirteenthAdvanceOnBirthday"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.thirteenthAdvanceOnBirthday}
                >
                  <InputLabel id="thirteenth-advance-on-birthday-label">
                    Antecipa na data de aniversário do colaborador
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="thirteenth-advance-on-birthday-label"
                    label="Antecipa na data de aniversário do colaborador"
                  >
                    {payrollOptions.thirteenthAdvanceOnBirthday.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.thirteenthAdvanceOnBirthday?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={thirteenthFirstInstallmentWithAverages === "5" ? 6 : 12}
          >
            <Controller
              name="thirteenthFirstInstallmentWithAverages"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.thirteenthFirstInstallmentWithAverages}
                >
                  <InputLabel id="thirteenth-first-installment-with-averages-label">
                    Pagamento da 1ª parcela com médias
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="thirteenth-first-installment-with-averages-label"
                    label="Pagamento da 1ª parcela com médias"
                  >
                    {payrollOptions.thirteenthFirstInstallmentWithAverages.map(
                      opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.thirteenthFirstInstallmentWithAverages?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          {thirteenthFirstInstallmentWithAverages === "5" && (
            <Grid item xs={12} md={6}>
              <Controller
                name="thirteenthFirstInstallmentWithAveragesOther"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required disabled={payrollIsValidated}
                    label="Descreva a Regra de Negócio"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!errors.thirteenthFirstInstallmentWithAveragesOther}
                    helperText={
                      errors.thirteenthFirstInstallmentWithAveragesOther?.message
                    }
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Controller
              name="internProLaboreThirteenth"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.internProLaboreThirteenth}
                >
                  <InputLabel id="intern-pro-labore-thirteenth-label">
                    Estagiário | Pró-labore
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="intern-pro-labore-thirteenth-label"
                    label="Estagiário | Pró-labore"
                  >
                    {payrollOptions.internProLaboreThirteenth.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.internProLaboreThirteenth?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          {internProLaboreThirteenth === "1" && <Grid
            item
            xs={12}
            md={
              ["1", "3", "4"].includes(internProLaboreThirteenthRubricsNovDec)
                ? 6
                : 12
            }
          >
            <Controller
              name="internProLaboreThirteenthRubricsNovDec"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.internProLaboreThirteenthRubricsNovDec}
                >
                  <InputLabel id="intern-pro-labore-thirteenth-rubrics-nov-dec-label">
                    Estagiário e pró-labore recebem 'gratificação natalina' na
                    Folha de Pagamento de NOV e DEZ
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="intern-pro-labore-thirteenth-rubrics-nov-dec-label"
                    label="Estagiário e pró-labore recebem 'gratificação natalina' na Folha de Pagamento de NOV e DEZ"
                  >
                    {payrollOptions.internProLaboreThirteenthRubricsNovDec.map(
                      opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.internProLaboreThirteenthRubricsNovDec?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>}
          {internProLaboreThirteenth === "1" && internProLaboreThirteenthRubricsNovDec === "1" && (
            <Grid item xs={12} md={6}>
              <Controller
                name="internProLaboreThirteenthRubricsDecReceive"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required disabled={payrollIsValidated}
                    label="Sob qual(is) verba(s)|rubrica(s)?"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!errors.internProLaboreThirteenthRubricsDecReceive}
                    helperText={
                      errors.internProLaboreThirteenthRubricsDecReceive?.message
                    }
                  />
                )}
              />
            </Grid>
          )}
          {internProLaboreThirteenth === "1" && internProLaboreThirteenthRubricsNovDec === "3" && (
            <Grid item xs={12} md={6}>
              <Controller
                name="internProLaboreThirteenthRubricsDecOnly"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required disabled={payrollIsValidated}
                    label="Sob qual(is) verba(s)|rubrica(s)?"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!errors.internProLaboreThirteenthRubricsDecOnly}
                    helperText={
                      errors.internProLaboreThirteenthRubricsDecOnly?.message
                    }
                  />
                )}
              />
            </Grid>
          )}
          {internProLaboreThirteenth === "1" && internProLaboreThirteenthRubricsNovDec === "4" && (
            <Grid item xs={12} md={6}>
              <Controller
                name="internProLaboreThirteenthOtherRule"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required disabled={payrollIsValidated}
                    label="Descreva a Regra de Negócio"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!errors.internProLaboreThirteenthOtherRule}
                    helperText={
                      errors.internProLaboreThirteenthOtherRule?.message
                    }
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <Controller
              name="thirteenthAnalyticalReportOrder"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.thirteenthAnalyticalReportOrder}
                >
                  <InputLabel id="thirteenth-analytical-report-order-label">
                    Relatório RH/DP Analítico por ordem de
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="thirteenth-analytical-report-order-label"
                    label="Relatório RH/DP Analítico por ordem de"
                  >
                    {payrollOptions.thirteenthAnalyticalReportOrder.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.thirteenthAnalyticalReportOrder?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="thirteenthSyntheticReportOrder"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.thirteenthSyntheticReportOrder}
                >
                  <InputLabel id="thirteenth-synthetic-report-order-label">
                    Relatório RH/DP Sintético por ordem de
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="thirteenth-synthetic-report-order-label"
                    label="Relatório RH/DP Sintético por ordem de"
                  >
                    {payrollOptions.thirteenthSyntheticReportOrder.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.thirteenthSyntheticReportOrder?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="thirteenthNetReport"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.thirteenthNetReport}
                >
                  <InputLabel id="thirteenth-net-report-label">
                    Relatório RH/DP Líquido - Consolidado (matriz|filial)
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="thirteenth-net-report-label"
                    label="Relatório RH/DP Líquido - Consolidado (matriz|filial)"
                  >
                    {payrollOptions.thirteenthNetReport.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.thirteenthNetReport?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="thirteenthNetReportExcel"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.thirteenthNetReportExcel}
                >
                  <InputLabel id="thirteenth-net-report-excel-label">
                    Relatório RH/DP Líquido no formato em Excel
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="thirteenth-net-report-excel-label"
                    label="Relatório RH/DP Líquido no formato em Excel"
                  >
                    {payrollOptions.thirteenthNetReportExcel.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.thirteenthNetReportExcel?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Folha de PLR
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasPlr"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasPlr}>
                  <InputLabel id="has-plr-label">
                    Possui folha de PLR
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="has-plr-label"
                    label="Possui folha de PLR"
                  >
                    {payrollOptions.hasPlr.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasPlr?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasPlr === "1" && (
            <>
              <Grid item xs={12} md={plrAccordingToCctAct === "2" ? 6 : 12}>
                <Controller
                  name="plrAccordingToCctAct"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.plrAccordingToCctAct}
                    >
                      <InputLabel id="plr-according-to-cct-act-label">
                        Conforme CCT|ACT
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="plr-according-to-cct-act-label"
                        label="Conforme CCT|ACT"
                      >
                        {payrollOptions.plrAccordingToCctAct.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.plrAccordingToCctAct?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              {plrAccordingToCctAct === "2" && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="plrBusinessRule"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required disabled={payrollIsValidated}
                        label="Beneficio da empresa, escreva a Regra de Negócio"
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={!!errors.plrBusinessRule}
                        helperText={errors.plrBusinessRule?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Controller
                  name="plrFrequencyAndCompetencies"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.plrFrequencyAndCompetencies}
                    >
                      <InputLabel id="plr-frequency-label">
                        Quantas vezes ao ano? (*)
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="plr-frequency-label"
                        label="Quantas vezes ao ano? (*)"
                      >
                        {["1", "2", "3", "4"].map(v => (
                          <MenuItem key={v} value={v}>{v}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.plrFrequencyAndCompetencies?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
                <Typography
                  sx={{ fontSize: "8px", color: "#7A7A7A", fontWeight: "400", ml: 1, mt: 0.5 }}
                >
                  (*) É vedado o pagamento de qualquer antecipação ou
                  distribuição de valores a título de participação nos lucros ou
                  resultados da empresa em mais de duas vezes no mesmo ano civil
                  e em periodicidade inferior a um trimestre civil. (Lei nº
                  10.101/2000, art. 3º, § 2º; Lei nº 12.832/2013, art. 1º)
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="plrSameCalendarYear"
                  control={control}
                  render={({ field }) => {
                    const months = [
                      { value: "1", label: "Janeiro" },
                      { value: "2", label: "Fevereiro" },
                      { value: "3", label: "Março" },
                      { value: "4", label: "Abril" },
                      { value: "5", label: "Maio" },
                      { value: "6", label: "Junho" },
                      { value: "7", label: "Julho" },
                      { value: "8", label: "Agosto" },
                      { value: "9", label: "Setembro" },
                      { value: "10", label: "Outubro" },
                      { value: "11", label: "Novembro" },
                      { value: "12", label: "Dezembro" },
                    ];
                    const maxSelections = parseInt(plrFrequencyAndCompetencies) || 4;
                    const selected = Array.isArray(field.value) ? field.value : [];
                    return (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disabled={payrollIsValidated}
                        options={months}
                        getOptionLabel={opt => opt.label}
                        value={months.filter(m => selected.includes(m.value))}
                        isOptionEqualToValue={(opt, val) => opt.value === val.value}
                        getOptionDisabled={opt =>
                          !selected.includes(opt.value) && selected.length >= maxSelections
                        }
                        onChange={(_, newValue) => {
                          field.onChange(newValue.map(m => m.value));
                        }}
                        renderOption={(props, option, { selected: isSelected }) => (
                          <li {...props}>
                            <Checkbox checked={isSelected} size="small" sx={{ mr: 1 }} />
                            <ListItemText primary={option.label} />
                          </li>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Em quais competências?"
                            size="small"
                            error={!!errors.plrSameCalendarYear}
                            helperText={errors.plrSameCalendarYear?.message}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="plrAnalyticalReportOrder"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.plrAnalyticalReportOrder}
                    >
                      <InputLabel id="plr-analytical-report-order-label">
                        Relatório RH/DP Analítico por ordem de
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="plr-analytical-report-order-label"
                        label="Relatório RH/DP Analítico por ordem de"
                      >
                        {payrollOptions.plrAnalyticalReportOrder.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.plrAnalyticalReportOrder?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="plrSyntheticReportOrder"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.plrSyntheticReportOrder}
                    >
                      <InputLabel id="plr-synthetic-report-order-label">
                        Relatório RH/DP Sintético por ordem de
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="plr-synthetic-report-order-label"
                        label="Relatório RH/DP Sintético por ordem de"
                      >
                        {payrollOptions.plrSyntheticReportOrder.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.plrSyntheticReportOrder?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="plrNetReport"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.plrNetReport}
                    >
                      <InputLabel id="plr-net-report-label">
                        Relatório RH/DP Líquido - Consolidado (matriz|filial)
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="plr-net-report-label"
                        label="Relatório RH/DP Líquido - Consolidado (matriz|filial)"
                      >
                        {payrollOptions.plrNetReport.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.plrNetReport?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="plrNetReportExcel"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.plrNetReportExcel}
                    >
                      <InputLabel id="plr-net-report-excel-label">
                        Relatório RH/DP Líquido no formato em Excel
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="plr-net-report-excel-label"
                        label="Relatório RH/DP Líquido no formato em Excel"
                      >
                        {payrollOptions.plrNetReportExcel.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.plrNetReportExcel?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="plrAccountingSyntheticReportOrder"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.plrAccountingSyntheticReportOrder}
                    >
                      <InputLabel id="plr-accounting-synthetic-report-order-label">
                        Relatório Contabilidade Sintético por ordem de
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="plr-accounting-synthetic-report-order-label"
                        label="Relatório Contabilidade Sintético por ordem de"
                      >
                        {payrollOptions.plrAccountingSyntheticReportOrder.map(
                          opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                      <FormHelperText>
                        {errors.plrAccountingSyntheticReportOrder?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Empréstimo Consignado
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasConsignedLoan"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required error={!!errors.hasConsignedLoan}
                >
                  <InputLabel id="has-consigned-loan-label">
                    Possui empréstimo consignado
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={payrollIsValidated}
                    labelId="has-consigned-loan-label"
                    label="Possui empréstimo consignado"
                  >
                    {payrollOptions.hasConsignedLoan.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.hasConsignedLoan?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasConsignedLoan === "1" && (
            <>
              <Grid item xs={12} md={consignedLoanDeductionLaw === "3" ? 6 : 12}>
                <Controller
                  name="consignedLoanDeductionLaw"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.consignedLoanDeductionLaw}
                    >
                      <InputLabel id="consigned-loan-deduction-law-label">
                        O desconto será conforme legislação vigente
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="consigned-loan-deduction-law-label"
                        label="O desconto será conforme legislação vigente"
                      >
                        {payrollOptions.consignedLoanDeductionLaw.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.consignedLoanDeductionLaw?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              {consignedLoanDeductionLaw === "3" && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="consignedLoanDeductionOther"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required disabled={payrollIsValidated}
                        label="Descreva a Regra de Negócio"
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={!!errors.consignedLoanDeductionOther}
                        helperText={errors.consignedLoanDeductionOther?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid
                item
                xs={12}
                md={consignedLoanFullDeductionIfNoBalance === "3" ? 6 : 12}
              >
                <Controller
                  name="consignedLoanFullDeductionIfNoBalance"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.consignedLoanFullDeductionIfNoBalance}
                    >
                      <InputLabel id="consigned-loan-full-deduction-if-no-balance-label">
                        Desconta o valor total da parcela, mesmo em caso de
                        Insuficiência de Saldo
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="consigned-loan-full-deduction-if-no-balance-label"
                        label="Desconta o valor total da parcela, mesmo em caso de Insuficiência de Saldo"
                      >
                        {payrollOptions.consignedLoanFullDeductionIfNoBalance.map(
                          opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                      <FormHelperText>
                        {errors.consignedLoanFullDeductionIfNoBalance?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              {consignedLoanFullDeductionIfNoBalance === "3" && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="consignedLoanFullDeductionIfNoBalanceOther"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required disabled={payrollIsValidated}
                        label="Descreva a Regra de Negócio"
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={
                          !!errors.consignedLoanFullDeductionIfNoBalanceOther
                        }
                        helperText={
                          errors.consignedLoanFullDeductionIfNoBalanceOther
                            ?.message
                        }
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Controller
                  name="consignedLoanFormat"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required error={!!errors.consignedLoanFormat}
                    >
                      <InputLabel id="consigned-loan-format-label">
                        Em que formato recebe a informação
                      </InputLabel>
                      <Select
                        {...field}
                        required disabled={payrollIsValidated}
                        labelId="consigned-loan-format-label"
                        label="Em que formato recebe a informação"
                      >
                        {payrollOptions.consignedLoanFormat.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.consignedLoanFormat?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="consignedLoanDataReceptionDate"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.consignedLoanDataReceptionDate}>
                      <InputLabel id="consigned-loan-data-reception-date-label">
                        Qual dia recebe a informação
                      </InputLabel>
                      <Select
                        {...field}
                        required
                        disabled={payrollIsValidated}
                        labelId="consigned-loan-data-reception-date-label"
                        label="Qual dia recebe a informação"
                      >
                        {Array.from({ length: 31 }, (_, i) => (
                          <MenuItem key={String(i + 1)} value={String(i + 1)}>
                            {i + 1}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.consignedLoanDataReceptionDate?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    </>
  );
}
